import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import sideImage1 from '../../Common/Images/sideimage1.gif';
import back_n from '../../Common/Images/back_n.gif';
import back_o from '../../Common/Images/back_o.gif';
import save_n from '../../Common/Images/save_n.gif';
import save_o from '../../Common/Images/save_o.gif'; 
import Loading from '../../Common/Loading';
import CustomAlert from '../../Common/CustomAlert';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface LessonSummary {
    id: string;
    name: string;
}

const EL_SetWorkPhase3: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const state = location.state || {};
    const { selectedLessons, returnPath, parentReturnPath } = state;

    const lessonPosition = parseInt(String(state.lessonPosition || 0), 10);
    const studentName = state.studentName || searchParams.get('StudentName');
    const familyCode = state.familyCode || searchParams.get('Work2FamilyCode');
    const login = state.login || searchParams.get('Work2Login');
    const gradeYear = state.gradeYear || searchParams.get('GradeYear');
    const schoolCode = state.schoolCode || searchParams.get('SchoolCode');

    const subject = state.subject || searchParams.get('Subject');
    const level = state.level || searchParams.get('Level');
    const workTitle = state.workTitle || searchParams.get('WorkTitle');

    const startDate = state.startDate || searchParams.get('StartDate');
    const finishDate = state.finishDate || searchParams.get('FinishDate');
    const startTime = state.startTime || searchParams.get('StartTime');
    const endTime = state.endTime || searchParams.get('EndTime');

    const workId = state.workId || searchParams.get('WorkID');
    const isEditMode = state.isEditMode || !!workId;

    const [isLoading, setIsLoading] = useState(false);
    const [isBackHovered, setIsBackHovered] = useState(false);
    const [isSaveHovered, setIsSaveHovered] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: '', isSuccess: false });

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const formatDateDisplay = (dateStr: string, timeStr: string) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year} ${timeStr}`;
    };

    const displayStart = formatDateDisplay(startDate, startTime);
    const displayFinish = formatDateDisplay(finishDate, endTime);

    const handleSave = async () => {
        setIsLoading(true);
        const currentTeacher = 'Tutor';
        try {
            if (lessonPosition > 0) {
                const lessonsPayload = selectedLessons.map((l: any) => ({
                    LessonID: l.id,
                    LessonName: l.name,
                    DICFile: level
                }));

                const response = await fetch(`${API_BASE_URL}api/wlp/add_lessons_to_plan/`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        SchoolCode: schoolCode,
                        FamilyCode: familyCode,
                        Login: login,
                        Subject: subject,
                        Lessons: lessonsPayload,
                        InsertAfterPosition: lessonPosition
                    })
                });

                const result = await response.json();
                
                if (response.ok) {
                    setAlertInfo({ show: true, message: result.message, isSuccess: true });
                } else {
                    setAlertInfo({ show: true, message: "Error: " + result.error, isSuccess: false });
                }
            } 
            else {
                const lessons = selectedLessons || [];
                const lessonIds = (lessons as LessonSummary[]).map(l => l.id);
                const fmtStart = `${startDate} ${startTime}`;
                const fmtFinish = `${finishDate} ${endTime}`;

                const payload = {
                    SchoolCode: schoolCode,
                    FamilyCode: familyCode,
                    Login: login,
                    Subject: subject,
                    Level: level,
                    Lessons: lessonIds,
                    WorkTitle: workTitle || undefined,
                    StartDate: fmtStart,
                    FinishDate: fmtFinish,
                    GradeYear: gradeYear,
                    TeacherLogin: currentTeacher,
                    WorkID: (isEditMode && workId) ? parseInt(workId.toString()) : 0,
                    IsEditMode: !!isEditMode
                };

                const response = await fetch(`${API_BASE_URL}api/save_work_phase2/`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                const result = await response.json();
                
                if (result.status === 'success') {
                    setAlertInfo({ show: true, message: result.message || "Work has been successfully saved.", isSuccess: true });
                } else {
                    setAlertInfo({ show: true, message: "Error: " + result.message, isSuccess: false });
                }
            }
        } catch (error) {
            console.error("Save failed", error);
            setAlertInfo({ show: true, message: "Failed to save due to a network error.", isSuccess: false });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseAlert = () => {
        const wasSuccess = alertInfo.isSuccess;
        setAlertInfo({ show: false, message: '', isSuccess: false });

        if (wasSuccess) {
            if (returnPath) {
                navigate(returnPath, { replace: true });
            } else {
                const params = new URLSearchParams({
                    SchoolCode: schoolCode || '',
                    Work2FamilyCode: familyCode || '',
                    Work2Login: login || '',
                    GradeYear: gradeYear || ''
                });

                navigate(`/SCHOOL/Teacher/EL_ViewWork?${params.toString()}`, { 
                    replace: true,
                    state: { 
                        returnPath: parentReturnPath
                    }
                });
            }
        }
    };

    const styles = {
        container: { 
            padding: '10px', fontFamily: 'Arial, sans-serif', backgroundColor: '#fff', fontSize: '12px', 
            minHeight: '97.8dvh', display: 'flex', flexDirection: 'column' as 'column'
        },
        mainLayout: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '15px',
            flex: 1,
            marginTop: '10px'
        },
        imageContainer: {
            flexShrink: 0,
            display: isMobile ? 'none' : 'block'
        },
        summaryTable: {
            width: '100%',
            borderCollapse: 'collapse' as 'collapse',
            fontFamily: 'Arial',
            fontSize: '12pt',
            borderBottom: '1px solid #E2E2E2',
            fontWeight: 'bold' as 'bold'
        },
        headerRow: { 
            backgroundColor: '#EBF1FD' 
        },
        headerCell: { 
            padding: '5px', 
            borderLeft: '1px solid #E2E2E2', 
            borderTop: '1px solid #E2E2E2', 
            borderRight: '1px solid #E2E2E2' 
        },
        labelCell: { 
            width: '150px', 
            padding: '5px', 
            borderLeft: '1px solid #E2E2E2', 
            borderTop: '1px solid #E2E2E2' 
        },
        valueCell: { 
            padding: '5px', 
            borderLeft: '1px solid #E2E2E2', 
            borderTop: '1px solid #E2E2E2', 
            borderRight: '1px solid #E2E2E2' 
        },
        activityContainer: {
            height: '300px', 
            overflowY: 'scroll' as 'scroll', 
            borderTop: '1px solid #E2E2E2', 
            borderRight: '0px',
        },
        activityTable: {
            width: '100%',
            borderCollapse: 'collapse' as 'collapse'
        },
        activityRow: {
            borderBottom: '1px solid #E2E2E2',
        },
        activityCell: {
            padding: '5px',
            borderLeft: '1px solid #E2E2E2',
            fontSize: '12pt'
        },
        
        footerTable: { width: '100%', marginTop: 'auto', border: '0px', flexShrink: 0 },
        footerBar: { backgroundColor: '#548BED', padding: '5px' },
    };

    return (
        <div style={styles.container}>
            {isLoading && <Loading />}
            
            <table id='tblHeader1' style={{ width:'100%', border: '0' }} cellPadding={0} cellSpacing={0}>
                <tbody>
                    <tr style={{ backgroundColor: '#E4E4E4' }}>
                        <td className='lableStyle' style={{ height: '26px', paddingLeft: '5px', verticalAlign: 'middle', fontFamily: 'Arial', fontSize: '13px', color: '#548BED', alignItems: 'left' }}>
                            eDealer: {familyCode} | Tutor: {studentName}
                        </td>
                        <td className='lableStyle' style={{ height: '26px', paddingRight: '15px', verticalAlign: 'middle', fontFamily: 'Arial', fontSize: '13px', color: '#548BED', textAlign: 'right' }}>
                            &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp; &nbsp;&nbsp;&nbsp;
                            <a href="#" id='lnkLogout' onClick={(e) => { 
                                e.preventDefault(); 
                                if (returnPath) {
                                    navigate(returnPath);
                                } else {
                                    const params = new URLSearchParams({
                                        SchoolCode: schoolCode || '',
                                        Work2FamilyCode: familyCode || '',
                                        Work2Login: login || '',
                                        GradeYear: gradeYear || ''
                                    });
                                    navigate(`/SCHOOL/Teacher/EL_ViewWork?${params.toString()}`, {
                                        state: { returnPath: parentReturnPath }
                                    });
                                }
                            }}>Cancel</a>
                        </td>
                    </tr>
                </tbody>
            </table>

            <table id='tblHeader2' style={{ width: '100%', border: '0', borderCollapse: 'collapse', marginTop: '3px', marginBottom: '15px' }}>
                <tbody>
                    <tr style={{ backgroundColor: '#E4E4E4', fontFamily: 'Arial', fontSize: '16px' }}>
                        <td className = 'PageHeader' style={{ height: '26px', verticalAlign: 'middle', paddingLeft: '5px', fontWeight: 'bold' }}>SET WORK</td>
                        <td style={{ height: '26px', verticalAlign: 'middle', textAlign: 'right', paddingRight: '15px' }}></td>
                    </tr>
                </tbody>
            </table>

            <div style={styles.mainLayout}>
                <div style={styles.imageContainer}>
                    <img src={sideImage1} alt="Side Decoration" />
                </div>

                <div style={{ flex: 1 }}>
                    <table style={styles.summaryTable} cellPadding={5} cellSpacing={0}>
                        <tbody>
                            <tr style={styles.headerRow}>
                                <td colSpan={2} style={styles.headerCell}>Set work summary</td>
                            </tr>
                            <tr>
                                <td style={styles.labelCell}>Title</td>
                                <td style={styles.valueCell}>{workTitle}</td>
                            </tr>
                            <tr>
                                <td style={styles.labelCell}>Assigned to</td>
                                <td style={styles.valueCell}>{studentName} from {familyCode} family</td>
                            </tr>
                            <tr>
                                <td style={styles.labelCell}>Subject</td>
                                <td style={styles.valueCell}>{subject}</td>
                            </tr>
                            <tr>
                                <td style={styles.labelCell}>Start date</td>
                                <td style={styles.valueCell}>{displayStart}</td>
                            </tr>
                            <tr>
                                <td style={{...styles.labelCell, borderBottom: '1px solid #E2E2E2'}}>Finish date</td>
                                <td style={{...styles.valueCell, borderBottom: '1px solid #E2E2E2'}}>{displayFinish}</td>
                            </tr>
                            <tr><td colSpan={2}>&nbsp;</td></tr>
                            
                            <tr>
                                <td style={styles.labelCell} valign="top">Work set:</td>
                                <td style={{padding: 0, verticalAlign: 'top', borderTop: '1px solid #E2E2E2', borderRight: '1px solid #E2E2E2'}}>
                                    <div style={styles.activityContainer}>
                                        <table style={styles.activityTable}>
                                            <tbody>
                                                {selectedLessons && selectedLessons.map((l: LessonSummary, idx: number) => (
                                                    <tr key={idx} style={styles.activityRow}>
                                                        <td style={styles.activityCell}>
                                                            {l.name.replace(/^\([GLT]\)\s*/, '')}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <table style={styles.footerTable}>
                <tbody>
                    <tr><td style={styles.footerBar}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 20px' }}>
                            <img 
                                src={isBackHovered ? back_o : back_n} 
                                alt="Back" 
                                onClick={() => {
                                    navigate({
                                        pathname: '/SCHOOL/Teacher/EL_SetWorkPhase2',
                                        search: `?${new URLSearchParams({
                                            SchoolCode: schoolCode,
                                            Work2FamilyCode: familyCode,
                                            Work2Login: login,
                                            GradeYear: gradeYear,
                                            WorkID: workId || ''
                                        }).toString()}`
                                    }, {
                                        state: location.state
                                    });
                                }}
                                onMouseEnter={() => setIsBackHovered(true)} 
                                onMouseLeave={() => setIsBackHovered(false)} 
                                style={{cursor: 'pointer'}}
                            />
                            <img 
                                src={isSaveHovered ? save_o : save_n} 
                                alt="Save" 
                                onClick={handleSave}
                                onMouseEnter={() => setIsSaveHovered(true)} 
                                onMouseLeave={() => setIsSaveHovered(false)} 
                                style={{cursor: 'pointer'}}
                            />
                        </div>
                    </td></tr>
                </tbody>
            </table>
            {alertInfo.show && (
                <CustomAlert 
                    message={alertInfo.message} 
                    onClose={handleCloseAlert} 
                />
            )}
        </div>
    );
};

export default EL_SetWorkPhase3;